import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  GouvDvfDataDto,
  GouvDvfMutationDto,
  PropertyFinancialsDto,
  PropertyId,
  PropertyLocationDto,
  PropertyResidenceType,
  PropertyType,
  UserApplications,
} from '@workspace/data-types';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';

@Component({
  selector: 'etxe-property-financials-display',
  imports: [CommonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
  templateUrl: './financials-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyFinancialsDisplayComponent {
  financials = input.required<PropertyFinancialsDto | undefined>();
  isInCoOwnership = input<boolean>(false);
  residenceType = input<PropertyResidenceType | undefined>();
  habitableSurface = input<number | undefined>();
  propertyDvf = input<GouvDvfDataDto | undefined>();
  propertyLocation = input<PropertyLocationDto | undefined>();
  propertyType = input<PropertyType | undefined>();
  propertyId = input<PropertyId | undefined>();

  private readonly destroyRef = inject(DestroyRef);
  private apiService = inject(ApiService);
  private readonly appStore = inject(ApplicationStore);
  private readonly userStore = inject(UserStore);

  readonly isAdminApp = computed(() => this.appStore.name() === UserApplications.ADMIN);
  private fetchedDvfData = signal<GouvDvfDataDto | null>(null);
  dvfData = computed(() => this.propertyDvf() ?? this.fetchedDvfData());
  isDvfLoading = signal<boolean>(false);

  private lastFetchedDvfKey: string | null = null;

  constructor() {
    effect(() => {
      if (this.propertyDvf() || this.fetchedDvfData()) {
        return;
      }
      const loc = this.propertyLocation();
      const codeInsee = loc?.address?.cityCode;
      const type = this.propertyType() || PropertyType.APARTMENT;
      const lat = loc?.coordinates?.latitude;
      const lon = loc?.coordinates?.longitude;

      if (!codeInsee) {
        return;
      }

      const key = `${codeInsee}_${type}_${lat}_${lon}`;
      if (this.lastFetchedDvfKey === key) {
        return;
      }
      this.lastFetchedDvfKey = key;

      untracked(() => {
        this.fetchDvfData(codeInsee, type, lat, lon);
      });
    });
  }

  private fetchDvfData(codeInsee: string, type: string, lat?: number, lon?: number) {
    if (this.propertyDvf()) {
      return;
    }

    const propId = this.propertyId();
    this.isDvfLoading.set(true);

    this.apiService
      .business()
      .etxe()
      .public()
      .gouv()
      .dvf(codeInsee, type, lat, lon)
      .pipe(
        catchError(() => of(null)),
        tap((res) => {
          if (res) {
            this.fetchedDvfData.set(res);
          }
        }),
        catchError(() => EMPTY),
        finalize(() => this.isDvfLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  isSecondaryResidence = computed(() => this.residenceType() === PropertyResidenceType.SECONDARY);

  pricePerSquareMeter = computed(() => {
    const price = this.financials()?.sellingPriceCents;
    const surface = this.habitableSurface();
    if (!price || !surface || surface <= 0) return null;
    return price / 100 / surface;
  });

  notaryFeesPercentage = computed(() => {
    const price = this.financials()?.sellingPriceCents;
    const fees = this.financials()?.taxesAndFees?.notaryFeesCents;

    if (!price || !fees || price === 0) return null;

    return (fees / price) * 100;
  });

  dvfEstimatedPrice = computed(() => {
    const dvf = this.dvfData();
    const surface = this.habitableSurface();
    if (!dvf?.medianPriceM2 || !surface || surface <= 0) return null;
    return Math.round(dvf.medianPriceM2 * surface);
  });

  dvfEstimatedPriceMin = computed(() => {
    const dvf = this.dvfData();
    const surface = this.habitableSurface();
    if (dvf?.minPriceM2 && surface && surface > 0) {
      return Math.round(dvf.minPriceM2 * surface);
    }
    const price = this.dvfEstimatedPrice();
    return price ? Math.round(price * 0.9) : null;
  });

  dvfEstimatedPriceMax = computed(() => {
    const dvf = this.dvfData();
    const surface = this.habitableSurface();
    if (dvf?.maxPriceM2 && surface && surface > 0) {
      return Math.round(dvf.maxPriceM2 * surface);
    }
    const price = this.dvfEstimatedPrice();
    return price ? Math.round(price * 1.1) : null;
  });

  priceComparison = computed(() => {
    const currentPriceM2 = this.pricePerSquareMeter();
    const dvf = this.dvfData();
    if (!currentPriceM2 || !dvf?.medianPriceM2) return null;

    const diffPercent = ((currentPriceM2 - dvf.medianPriceM2) / dvf.medianPriceM2) * 100;
    const absDiff = Math.abs(Math.round(diffPercent));

    if (Math.abs(diffPercent) <= 3) {
      return {
        label: 'Prix aligné sur la médiane DVF du secteur (±3 %)',
        colorClass: 'text-jade-11 bg-jade-a3 border-jade-6',
        icon: 'check_circle',
        badge: 'Dans le marché',
      };
    } else if (diffPercent > 3) {
      return {
        label: `+${absDiff} % par rapport au prix médian DVF du secteur`,
        colorClass:
          diffPercent > 15
            ? 'text-amber-11 bg-amber-a3 border-amber-6'
            : 'text-indigo-11 bg-indigo-a3 border-indigo-6',
        icon: 'trending_up',
        badge: `+${absDiff} %`,
      };
    } else {
      return {
        label: `-${absDiff} % par rapport au prix médian DVF du secteur`,
        colorClass: 'text-teal-11 bg-teal-a3 border-teal-6',
        icon: 'trending_down',
        badge: `-${absDiff} %`,
      };
    }
  });

  isRecentSalesExpanded = signal<boolean>(false);

  allRecentSales = computed<GouvDvfMutationDto[]>(() => {
    const sales = this.dvfData()?.recentSales ?? [];
    return [...sales].sort((a, b) => (b.date_mutation || '').localeCompare(a.date_mutation || ''));
  });

  displayedRecentSales = computed<GouvDvfMutationDto[]>(() => {
    const all = this.allRecentSales();
    return this.isRecentSalesExpanded() ? all : all.slice(0, 3);
  });

  toggleRecentSalesExpand() {
    this.isRecentSalesExpanded.update((v) => !v);
  }
}
